// Community -> Live Activity -> INDIVIDUAL VISITOR & USER ACTIVITY.
// The ONE new top-level table of the tab. One row = one identifiable browser
// installation (never one person: a person with a phone and a laptop has two
// rows sharing an account). Rows only carry counters; histories are loaded
// when a record is opened (VisitorDetail). Nothing is requested until Family
// opens the section, and auto refresh only runs while the browser tab is
// visible. Which columns exist is decided by the server-side checkboxes
// (Settings -> Individual Activity Tracking).
// Two views over the same records: [BY VISITOR] (one row per browser) and
// [BY USER] (AccountActivityTable, one row per account). The view is only
// changed by Family's click and remembered in this browser.
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaChevronDown, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { getIndividualSummaryAPI, getIndividualVisitorsAPI } from "../../../auth/liveApi";
import { elakayAutoText } from "../../../pageInside/dataElakay";
import { roleDisplayLabel } from "../../../../utils/roleDisplayLabel";
import { ROLE_KEYS, ROLE_LABELS, formatCount } from "../LiveParts";
import { REFERRER_LABELS, TRAFFIC_LABELS, visibleColumns } from "./individualFields";
import { formatInZone, shortId } from "./individualFormat";
import { RiskBadge, StatusBadge, TypeBadge } from "./IndividualBadges";
import VisitorDetail from "./VisitorDetail";
import AccountActivityTable from "./AccountActivityTable";
import "./individual.css";

const STORAGE_KEY = "elakay_individual_activity";
const VIEW_KEY = "elakay_individual_activity_view";
const VIEWS = ["visitor", "user"];
const SETTINGS_URL = "/elakay/Settings?tab=individual-activity";
const DEFAULT_FILTERS = { q: "", from: "", to: "", type: "", country: "", deviceType: "", role: "", online: "", security: "", traffic: "", sort: "lastActivity", dir: "desc", limit: 25 };
const AUTO_REFRESH_MS = 30 * 1000;
const DEVICE_TYPES = ["Desktop", "Mobile", "Tablet", "Bot", "Unknown"];

const readSaved = () => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "{}");
    return { open: saved.open === true, auto: saved.auto === true, filters: { ...DEFAULT_FILTERS, ...(saved.filters || {}) } };
  } catch {
    return { open: false, auto: false, filters: { ...DEFAULT_FILTERS } };
  }
};

// The admin's own choice only: never switched automatically. Without a stored
// choice the table opens on "By visitor".
const readView = () => {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return VIEWS.includes(v) ? v : "visitor";
  } catch {
    return "visitor";
  }
};

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
};

const SortHeader = ({ column, filters, onSort }) => {
  if (!column.sort) return column.label;
  const active = filters.sort === column.sort;
  return (
    <button type="button" className={`ia-sort ${active ? "is-active" : ""}`} onClick={() => onSort(column.sort)}>
      {column.label}{active ? (filters.dir === "asc" ? " ▲" : " ▼") : ""}
    </button>
  );
};

const Cell = ({ column, row, tz, t }) => {
  switch (column.key) {
    case "visitor":
      return (
        <div className="ia-cell-main">
          <span className="ia-id" title={row.visitorId}>{shortId(row.visitorId)}</span>
          <small><TypeBadge type={row.visitorType} t={t} /></small>
        </div>
      );
    case "account": {
      const a = row.associatedAccounts?.[0];
      if (!a) return <span className="live-muted">—</span>;
      return (
        <div className="ia-cell-main">
          <span>{a.name}</span>
          <small>{a.email}{row.accountRole || a.role ? ` · ${roleDisplayLabel(row.accountRole || a.role)}` : ""}</small>
          {row.associatedAccounts.length > 1 && <small>+{row.associatedAccounts.length - 1} {t("moreAccounts", "more account(s)")}</small>}
        </div>
      );
    }
    case "status": return <StatusBadge status={row.status} t={t} />;
    case "firstSeen": return formatInZone(row.firstSeenAt, tz);
    case "lastSeen": return formatInZone(row.lastSeenAt, tz);
    case "location": {
      const parts = [row.geo?.city, row.geo?.state, row.geo?.country].filter((v) => v && v !== "Unknown");
      return parts.length ? parts.join(", ") : <span className="live-muted">{t("unknown", "Unknown")}</span>;
    }
    case "device": {
      const parts = [row.device?.deviceType, row.device?.os, row.device?.browser].filter(Boolean);
      return parts.length ? parts.join(" · ") : <span className="live-muted">—</span>;
    }
    case "visits": return formatCount(row.totals?.visits);
    case "sessions": return formatCount(row.totals?.sessions);
    case "pageViews": return formatCount(row.totals?.pageViews);
    case "logins": return formatCount(row.logins?.success);
    case "failed": return row.logins?.failed ? <strong className="live-error">{formatCount(row.logins.failed)}</strong> : "0";
    case "referrer":
      return row.referrer?.category
        ? <div className="ia-cell-main"><span>{REFERRER_LABELS[row.referrer.category] || row.referrer.category}</span><small>{row.referrer.host}</small></div>
        : <span className="live-muted">{t("direct", "Direct / none")}</span>;
    case "traffic": return row.traffic?.class ? <span className={`ia-badge t-${row.traffic.class}`}>{TRAFFIC_LABELS[row.traffic.class]}</span> : "—";
    case "security":
      return (
        <div className="ia-cell-main">
          <RiskBadge level={row.riskLevel} t={t} />
          <small>{formatCount(row.securityEventCount)} {t("events", "event(s)")}{row.restrictionStatus && row.restrictionStatus !== "none" ? ` · ${t(`restr_${row.restrictionStatus}`, row.restrictionStatus.replace(/_/g, " "))}` : ""}</small>
        </div>
      );
    default: return null;
  }
};

const QuickDetails = ({ row, tz, t, onOpen }) => (
  <div className="ia-quick">
    <div>
      <h4>{t("qAccounts", "Accounts on this browser")}</h4>
      {row.associatedAccounts?.length
        ? <ul>{row.associatedAccounts.map((a) => <li key={a.userId}><strong>{a.name}</strong> <small className="live-muted">{a.email} · {roleDisplayLabel(a.role)}</small></li>)}</ul>
        : <p className="ia-note">{t("qNoAccount", "No account has signed in on this browser.")}</p>}
    </div>
    <div>
      <h4>{t("qActivity", "Activity")}</h4>
      <ul>
        {row.logins?.lastLoginAt !== undefined && <li>{t("lastLogin", "Last login")}: {formatInZone(row.logins.lastLoginAt, tz)}</li>}
        {row.logins?.lastLogoutAt !== undefined && <li>{t("lastLogout", "Last logout")}: {formatInZone(row.logins.lastLogoutAt, tz)}</li>}
        {row.activeDays !== undefined && <li>{t("activeDays", "Active days")}: {formatCount(row.activeDays)}</li>}
        <li>{t("lastActivity", "Last recorded activity")}: {formatInZone(row.lastActivityAt, tz)}</li>
      </ul>
    </div>
    {row.traffic?.reasons?.length > 0 && (
      <div>
        <h4>{t("qTraffic", "Why this traffic class")}</h4>
        <ul>{row.traffic.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
      </div>
    )}
    <div className="ia-quick-actions">
      <button type="button" className="live-btn" onClick={() => onOpen("overview")}>{t("openRecord", "Open full record")}</button>
      <button type="button" className="live-btn live-btn-ghost" onClick={() => onOpen("historypage")}>Historypage</button>
      <button type="button" className="live-btn live-btn-ghost" onClick={() => onOpen("historylog")}>Historylog</button>
    </div>
  </div>
);

const SummaryStrip = ({ summary, t }) => {
  const s = summary || {};
  const stat = (label, value, hint, cls = "") => (
    <div className={`ia-stat ${cls}`}><span>{label}</span><strong>{summary ? formatCount(value) : "—"}</strong>{hint && <small>{hint}</small>}</div>
  );
  return (
    <div className="ia-summary" aria-label={t("summary", "Summary")}>
      {stat(t("sumVisitors", "Identifiable visitors"), s.uniqueVisitors, t("sumVisitorsHint", "browser records"))}
      {stat(t("sumAnonymous", "Anonymous"), s.anonymousVisitors, t("sumAnonymousHint", "never signed in"))}
      {stat(t("sumLinked", "Linked to an account"), s.linkedVisitors, `${formatCount(s.distinctAccounts)} ${t("sumAccounts", "accounts")}`)}
      {stat(t("sumOnline", "Online now"), s.onlineNow, null, "is-good")}
      {stat(t("sumSessions", "Sessions"), s.sessions)}
      {stat(t("sumPageViews", "Page views"), s.pageViews)}
      {stat(t("sumHuman", "Likely human"), s.likelyHuman)}
      {stat(t("sumAutomated", "Automated (est.)"), s.automatedSuspected, null, "is-warn")}
      {stat(t("sumSuspicious", "Suspicious"), s.suspicious, null, s.suspicious ? "is-bad" : "")}
      {stat(t("sumUnclassified", "Unclassified"), s.unclassified)}
    </div>
  );
};

const IndividualActivityTable = ({ animations = true }) => {
  const t = useCallback((key, fallback, values) => elakayAutoText(`h_ia_${key}`, fallback, values), []);
  const saved = useMemo(readSaved, []);
  const [open, setOpen] = useState(saved.open);
  const [auto, setAuto] = useState(saved.auto);
  const [filters, setFilters] = useState(saved.filters);
  const [qInput, setQInput] = useState(saved.filters.q);
  const q = useDebounced(qInput, 400);
  const [cursors, setCursors] = useState([null]); // cursor of each visited page
  const [res, setRes] = useState({ loading: false, error: null, data: null });
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [detail, setDetail] = useState(null); // { vk, tab }
  const [tick, setTick] = useState(0);
  const [view, setView] = useState(readView);
  const pageCursor = cursors[cursors.length - 1];

  useEffect(() => {
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ open, auto, filters })); } catch { /* optional */ }
  }, [open, auto, filters]);
  // Any filter change restarts from the first page.
  const applyFilters = useCallback((next) => {
    setFilters(next);
    setCursors([null]);
    setExpanded(new Set());
  }, []);
  const chooseView = (next) => {
    setView(next);
    try { window.localStorage.setItem(VIEW_KEY, next); } catch { /* optional */ }
  };
  // Only once the typing has settled (a reset must not re-apply the old text).
  useEffect(() => { if (q === qInput && q !== filters.q) applyFilters({ ...filters, q }); }, [q, qInput, filters, applyFilters]);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    getIndividualSummaryAPI({}, { signal: controller.signal }).then((s) => setSummary(s.summary)).catch(() => {});
    return () => controller.abort();
  }, [open, tick]);

  useEffect(() => {
    if (!open || view !== "visitor") return undefined;
    const controller = new AbortController();
    setRes((r) => ({ ...r, loading: true, error: null }));
    const params = { ...filters, ...(pageCursor ? { cursor: pageCursor } : {}) };
    getIndividualVisitorsAPI(params, { signal: controller.signal })
      .then((data) => setRes({ loading: false, error: null, data }))
      .catch((e) => {
        if (e.name === "AbortError") return;
        const msg = e.status === 403 ? t("forbidden", "Reserved to the Developer (Family) role.") : e.message || t("loadError", "Could not load the activity table.");
        setRes((r) => ({ ...r, loading: false, error: msg }));
      });
    return () => controller.abort();
  }, [open, view, filters, pageCursor, tick, t]);
  // Filters shared by both views (the visitor-only sort and cursor excluded).
  const accountFilters = useMemo(() => {
    const { limit: _limit, ...rest } = filters;
    return rest;
  }, [filters]);

  // Auto refresh: only while this browser tab is visible.
  const timer = useRef(null);
  useEffect(() => {
    if (!open || !auto) return undefined;
    timer.current = setInterval(() => { if (document.visibilityState === "visible") setTick((n) => n + 1); }, AUTO_REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [open, auto]);

  const set = (patch) => applyFilters({ ...filters, ...patch });
  const onSort = (sort) => set(filters.sort === sort ? { dir: filters.dir === "asc" ? "desc" : "asc" } : { sort, dir: "desc" });
  const data = res.data;
  const fields = data?.fields || {};
  const columns = visibleColumns(fields);
  const tz = data?.timezone;
  const countries = useMemo(() => [...new Set((data?.rows || []).map((r) => r.geo?.country).filter((c) => c && c !== "Unknown"))].sort(), [data]);
  const toggle = (vk) => setExpanded((prev) => { const next = new Set(prev); if (next.has(vk)) next.delete(vk); else next.add(vk); return next; });
  const page = cursors.length;
  const start = data ? (page - 1) * data.limit + (data.rows.length ? 1 : 0) : 0;
  const end = data ? (page - 1) * data.limit + data.rows.length : 0;

  return (
    <section className={`live-card ia-root ${animations ? "" : "ia-no-anim"}`} aria-labelledby="ia-title">
      <header className="ia-head">
        <div>
          <h3 id="ia-title">{t("title", "INDIVIDUAL VISITOR & USER ACTIVITY")}</h3>
          <p className="live-muted">{t("subtitle", "One row per identifiable browser installation. A person using several browsers or devices has several rows, linked when they sign in. No fingerprinting: clearing the browser storage creates a new record.")}</p>
        </div>
        <div className="live-actions">
          {open && <Link className="live-btn live-btn-ghost" to={SETTINGS_URL}>{t("settings", "Tracking settings")}</Link>}
          <button type="button" className="live-btn live-btn-ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls="ia-body">
            {open ? t("hide", "Hide") : t("show", "Show individual activity")}
          </button>
        </div>
      </header>

      {open && (
        <div id="ia-body">
          {data && !data.saveHistory && (
            <div className="ia-banner ia-banner-off" role="status">
              <span>{t("offBanner", "“Save Visitor History” is OFF: nothing new is being recorded. Previously recorded history stays available below.")} <Link to={SETTINGS_URL}>{t("turnOn", "Open settings")}</Link></span>
            </div>
          )}
          <div className="ia-views" role="tablist" aria-label={t("viewLabel", "Group the activity")}>
            {[["visitor", t("byVisitor", "By visitor")], ["user", t("byUser", "By user")]].map(([key, label]) => (
              <button key={key} type="button" role="tab" id={`ia-view-${key}`} aria-selected={view === key} aria-controls="ia-view-panel" className={view === key ? "is-active" : ""} onClick={() => chooseView(key)}>{label}</button>
            ))}
          </div>
          <SummaryStrip summary={summary} t={t} />

          <form className="ia-filters" onSubmit={(e) => e.preventDefault()} aria-label={t("filters", "Filters")}>
            <label className="ia-search"><span>{t("search", "Search")}</span>
              <input type="search" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder={t("searchPh", "Visitor id (v_…), name or e-mail…")} />
            </label>
            <label><span>{t("from", "Active from")}</span><input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => set({ from: e.target.value })} /></label>
            <label><span>{t("to", "Active to")}</span><input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => set({ to: e.target.value })} /></label>
            {view === "visitor" && (
              <label><span>{t("fType", "Visitor / user")}</span>
                <select value={filters.type} onChange={(e) => set({ type: e.target.value })}>
                  <option value="">{t("all", "All")}</option>
                  <option value="anonymous">{t("vAnonymous", "Anonymous")}</option>
                  <option value="authenticated">{t("vAuthenticated", "Signed in")}</option>
                  <option value="signed_out">{t("vSignedOut", "Signed out")}</option>
                  <option value="linked">{t("vLinked", "Linked to an account")}</option>
                </select>
              </label>
            )}
            <label><span>{t("fRole", "Account role")}</span>
              <select value={filters.role} onChange={(e) => set({ role: e.target.value })}>
                <option value="">{t("allRoles", "All roles")}</option>
                {ROLE_KEYS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </label>
            {fields.country !== false && (
              <label><span>{t("fCountry", "Country")}</span>
                <input list="ia-countries" value={filters.country} onChange={(e) => set({ country: e.target.value })} placeholder={t("anyCountry", "Any")} />
                <datalist id="ia-countries">{countries.map((c) => <option key={c} value={c} />)}</datalist>
              </label>
            )}
            {fields.deviceType !== false && (
              <label><span>{t("fDevice", "Device")}</span>
                <select value={filters.deviceType} onChange={(e) => set({ deviceType: e.target.value })}>
                  <option value="">{t("all", "All")}</option>
                  {DEVICE_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </label>
            )}
            <label><span>{t("fOnline", "Online / offline")}</span>
              <select value={filters.online} onChange={(e) => set({ online: e.target.value })}>
                <option value="">{t("all", "All")}</option>
                <option value="online">{t("sOnline", "Online")}</option>
                <option value="offline">{t("sOffline", "Offline")}</option>
              </select>
            </label>
            <label><span>{t("fSecurity", "Security status")}</span>
              <select value={filters.security} onChange={(e) => set({ security: e.target.value })}>
                <option value="">{t("all", "All")}</option>
                <option value="with_events">{t("withEvents", "With security events")}</option>
                <option value="restricted">{t("restricted", "Account restricted / blocked")}</option>
              </select>
            </label>
            {fields.optionalMetrics !== false && (
              <label><span>{t("fTraffic", "Traffic class")}</span>
                <select value={filters.traffic} onChange={(e) => set({ traffic: e.target.value })}>
                  <option value="">{t("all", "All")}</option>
                  <option value="humans">{t("tHumansOnly", "Humans only (hide automated)")}</option>
                  <option value="automated_any">{t("tAutomatedAny", "Automated / bots only")}</option>
                  {Object.keys(TRAFFIC_LABELS).map((k) => <option key={k} value={k}>{TRAFFIC_LABELS[k]}</option>)}
                </select>
              </label>
            )}
          </form>

          <div className="ia-toolbar">
            <span className="live-muted" aria-live="polite">
              {view === "visitor" && data ? t("showing", "{value0}–{value1} of {value2} records", { value0: formatCount(start), value1: formatCount(end), value2: formatCount(data.total) }) : ""}
            </span>
            <div className="live-actions">
              <label className="live-muted" htmlFor="ia-limit">{t("perPage", "Per page")}</label>
              <select id="ia-limit" value={filters.limit} onChange={(e) => set({ limit: Number(e.target.value) })} className="ia-limit">
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <label className="ia-check" style={{ padding: "4px 6px" }}>
                <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
                <span><small>{t("autoRefresh", "Auto refresh (30 s)")}</small></span>
              </label>
              <button type="button" className="live-btn live-btn-ghost" onClick={() => setTick((n) => n + 1)} disabled={res.loading} aria-label={t("refresh", "Refresh")}>
                <FaSyncAlt aria-hidden="true" /> {t("refresh", "Refresh")}
              </button>
              <button type="button" className="live-btn live-btn-ghost" onClick={() => { setQInput(""); applyFilters({ ...DEFAULT_FILTERS }); }}>{t("reset", "Reset filters")}</button>
            </div>
          </div>

          {view === "user" ? (
            <div id="ia-view-panel" role="tabpanel" aria-labelledby="ia-view-user">
              <AccountActivityTable filters={accountFilters} limit={filters.limit} tick={tick} t={t} onOpenVisitor={(vk) => setDetail({ vk, tab: "overview" })} />
            </div>
          ) : (
            <div id="ia-view-panel" role="tabpanel" aria-labelledby="ia-view-visitor">
              {res.error && <div className="ia-state is-error" role="alert"><strong>{t("errorTitle", "Something went wrong")}</strong><span>{res.error}</span><button type="button" className="live-btn live-btn-ghost" onClick={() => setTick((n) => n + 1)}>{t("retry", "Retry")}</button></div>}
              {!res.error && !data && res.loading && (
                <div className="ia-state" aria-busy="true"><span className="live-sr">{t("loading", "Loading…")}</span>{[1, 2, 3, 4].map((i) => <div key={i} className="ia-skeleton" style={{ width: `${90 - i * 12}%` }} />)}</div>
              )}
              {!res.error && data && !data.rows.length && (
                <div className="ia-state">
                  <strong>{t("emptyTitle", "No visitor record matches")}</strong>
                  <span>{data.total === 0 && !data.saveHistory ? t("emptyOff", "Turn “Save Visitor History” ON in the tracking settings to start recording.") : t("emptyHint", "Change or reset the filters.")}</span>
                </div>
              )}
              {!res.error && data && data.rows.length > 0 && (
                <div className="ia-table-wrap" aria-busy={res.loading}>
                  <table className="ia-table">
                    <caption className="live-sr">{t("caption", "Individual visitor and user activity, one row per browser")}</caption>
                    <thead>
                      <tr>{columns.map((c) => (
                        <th key={c.key} scope="col" className={c.num ? "num" : ""} aria-sort={c.sort ? (filters.sort === c.sort ? (filters.dir === "asc" ? "ascending" : "descending") : "none") : undefined}>
                          <SortHeader column={c} filters={filters} onSort={onSort} />
                        </th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {data.rows.map((row) => {
                        const isOpen = expanded.has(row.visitorId);
                        return (
                          <Fragment key={row.visitorId}>
                            <tr
                              className={`ia-row ${isOpen ? "is-expanded" : ""}`}
                              tabIndex={0}
                              onClick={() => setDetail({ vk: row.visitorId, tab: "overview" })}
                              onKeyDown={(e) => { if (e.key === "Enter") setDetail({ vk: row.visitorId, tab: "overview" }); }}
                            >
                              {columns.map((c, i) => (
                                <td key={c.key} data-label={c.label} className={c.num ? "num" : ""}>
                                  {i === 0 ? (
                                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                                      <button type="button" className="ia-caret" aria-expanded={isOpen} aria-label={isOpen ? t("collapse", "Collapse details") : t("expand", "Expand details")} onClick={(e) => { e.stopPropagation(); toggle(row.visitorId); }}>
                                        {isOpen ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                                      </button>
                                      <Cell column={c} row={row} tz={tz} t={t} />
                                    </div>
                                  ) : <Cell column={c} row={row} tz={tz} t={t} />}
                                </td>
                              ))}
                            </tr>
                            {isOpen && (
                              <tr className="ia-expand"><td colSpan={columns.length}><QuickDetails row={row} tz={tz} t={t} onOpen={(tab) => setDetail({ vk: row.visitorId, tab })} /></td></tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {data && (data.nextCursor || cursors.length > 1) && (
                <nav className="ia-pager" aria-label={t("pagination", "Pagination")}>
                  <button type="button" className="live-btn live-btn-ghost" disabled={cursors.length <= 1 || res.loading} onClick={() => setCursors((c) => c.slice(0, -1))}>{t("previous", "Previous")}</button>
                  <span className="live-muted">{t("page", "Page {value0}", { value0: page })}</span>
                  <button type="button" className="live-btn live-btn-ghost" disabled={!data.nextCursor || res.loading} onClick={() => setCursors((c) => [...c, data.nextCursor])}>{t("next", "Next")}</button>
                </nav>
              )}
            </div>
          )}
        </div>
      )}

      {detail && <VisitorDetail visitorKey={detail.vk} initialTab={detail.tab} onHide={() => setDetail(null)} onChanged={() => setTick((n) => n + 1)} t={t} />}
    </section>
  );
};

export default IndividualActivityTable;
